import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { runTask } from 'ember-lifeline';
import { eq } from 'ember-truth-helpers';
import LoginWizardEmailFound from 'garaje/components/login/login-wizard/email-found';
import LoginWizardEmailSearch from 'garaje/components/login/login-wizard/email-search';
import LoginWizardMfaChallenge from 'garaje/components/login/login-wizard/mfa-challenge';
import UiCard from 'garaje/components/ui/card';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type SessionsService from 'garaje/services/session';
import type WindowLocationService from 'garaje/services/window-location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { LoginSteps, type LoginStep } from 'garaje/utils/login-steps';
import urlBuilder from 'garaje/utils/url-builder';
import type { SinglePayload } from 'jsonapi/response';
import LiquidBind from 'liquid-fire/components/liquid-bind';
import { localCopy } from 'tracked-toolbox';

interface MfaChallengeResponse {
  challenge_code_expires_at: number;
}

interface LoginWizardSignature {
  Args: {
    /** Company ID for authentication */
    companyId: number;
    /** Pre-filled email address from URL parameters */
    prefilledEmail: string;
    /** Redirect destination parameter */
    rd: string;
    /** Function to redirect to Envoy web application */
    redirectToEnvoy: (path: string) => void;
    /** State parameter for SAML authentication */
    state: string;
    /** Current login step @default LoginSteps.emailSearch */
    step?: LoginStep;
  };
  Element: HTMLDivElement;
}

/**
 * Login wizard component that handles the multi-step authentication flow
 */
export default class LoginWizardComponent extends Component<LoginWizardSignature> {
  @service declare ajax: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare localStorage: LocalStorageService;
  @service declare session: SessionsService;
  @service declare windowLocation: WindowLocationService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @localCopy('args.step', LoginSteps.emailSearch) currentStep!: LoginStep;
  @tracked emailFound = false;
  @tracked password = '';
  @tracked email = '';
  @tracked _samlUrl?: string | null;

  constructor(owner: unknown, args: LoginWizardSignature['Args']) {
    super(owner, args);

    if (args.step === LoginSteps.mfaChallenge) {
      void this.sendMfaCode.perform();
    }

    // Use delay to break-up attribute changes into separate computations to avoid error.
    runTask(
      this,
      () => {
        this.showSessionTimeoutFlashMsg();
      },
      1,
    );
  }

  get samlUrl(): string | null | undefined {
    return this._samlUrl;
  }

  set samlUrl(url: string | undefined) {
    try {
      const samlUrl = new URL(url!);
      // pass through the state parameter if it's present
      if (this.args.state) {
        // eslint-disable-next-line ember/use-ember-get-and-set
        samlUrl.searchParams.set('state', this.args.state);
      }
      this._samlUrl = samlUrl.toString();
    } catch {
      // unset samlUrl if it's not a valid URL
      this._samlUrl = null;
    }
  }

  showSessionTimeoutFlashMsg(): void {
    const sessionTimeout = this.localStorage.getItem('sessionTimeout');

    this.localStorage.setItem('sessionTimeout', 'false');

    if (sessionTimeout === 'true') {
      this.flashMessages.showFlash(
        'warning',
        'Session timeout',
        'You have been logged out due to the inactivity limit set by your admin.',
      );
    }
  }

  moveTo = (step: LoginStep, email: string): void => {
    this.email = email;
    this.currentStep = step;
  };

  handleSearchResult = (
    email: string,
    found: boolean,
    payload?: SinglePayload<{ usesCredentials: boolean; samlTo: string; confirmed: boolean }>,
  ): void => {
    if (!found) {
      this.flashMessages.showAndHideFlash('error', 'Login failed');
      return;
    }

    const attributes = payload?.data.attributes || {};
    const canUseCredentials = Boolean(attributes['uses-credentials']);
    const canUseSAML = Boolean(attributes['saml-to']);
    const isConfirmed = Boolean(attributes.confirmed);

    this.email = email;
    this.samlUrl = attributes['saml-to'];

    this.#handleSearchResult({ canUseCredentials, canUseSAML, isConfirmed });
  };

  authenticateTask = dropTask(async (email: string, password: string): Promise<void> => {
    // update local values
    this.email = email;
    this.password = password;

    const params = {
      companyId: this.args.companyId,
      email: email,
      password: password,
      redirectFrom: this.args.rd,
    };

    try {
      await this.session.authenticate('authenticator:application', params);
      this.workplaceMetrics.trackEvent('DASHBOARD_LOG_IN_SUCCESS', {
        companyId: this.args.companyId,
        email: email,
      });
    } catch (error) {
      if ((error as { error?: string })?.error === 'mfa_required') {
        // HTTP 403 response with `{ error: "mfa_required" }` in the body means we should show the MFA challenge page
        void this.sendMfaCode.perform();
        this.#displayMFAChallenge();
      } else {
        // otherwise, presume invalid credentials
        this.flashMessages.showAndHideFlash('error', 'Email and password do not match');
        this.password = '';
        (<HTMLElement>document.getElementsByClassName('password-field')[0]).focus();
      }
    }
  });

  verifyMfaCode = dropTask(async (mfaCode: string, rememberDevice: boolean, quiet = false): Promise<void> => {
    const params = {
      companyId: this.args.companyId,
      redirectFrom: this.args.rd,
      mfaCode,
      rememberDevice,
    };

    try {
      await this.session.authenticate('authenticator:application', params);
      this.flashMessages.hideAll();
      this.workplaceMetrics.trackEvent('DASHBOARD_LOG_IN_SUCCESS', {
        companyId: this.args.companyId,
        email: this.email,
      });
    } catch {
      if (quiet) return;

      this.flashMessages.showFlash('error', 'Invalid code. Please try again or request a new code.');
    }
  });

  sendMfaCode = dropTask(async (): Promise<void> => {
    try {
      const url = urlBuilder.authn.mfaChallenge();
      await this.ajax.post<MfaChallengeResponse>(url);

      this.flashMessages.showAndHideFlash('success', 'Code sent!');
    } catch {
      this.flashMessages.showFlash('error', 'Error sending code. Try again later.');
    }
  });

  #handleSearchResult({
    canUseCredentials,
    canUseSAML,
    isConfirmed,
  }: {
    canUseCredentials: boolean;
    canUseSAML: boolean;
    isConfirmed: boolean;
  }): void {
    switch (true) {
      case isConfirmed && canUseCredentials && canUseSAML:
        this._displayPasswordForm();
        break;
      case isConfirmed && !canUseCredentials && canUseSAML:
        this._redirectToSAML();
        break;
      case isConfirmed && canUseCredentials && !canUseSAML:
        this._displayPasswordForm();
        break;
      case isConfirmed && !canUseCredentials && !canUseSAML:
        this._displayConfigIncompleteError();
        break;
      case !isConfirmed && canUseCredentials && canUseSAML:
        this._displayPasswordForm();
        break;
      case !isConfirmed && !canUseCredentials && canUseSAML:
        this._redirectToSAML();
        break;
      case !isConfirmed && canUseCredentials && !canUseSAML:
        this._displayResendConfirmationEmail();
        break;
      case !isConfirmed && !canUseCredentials && !canUseSAML:
        this._displayConfigIncompleteError();
        break;
      default:
        this._displayConfigIncompleteError();
    }
  }

  changeEmail = (): void => {
    this.emailFound = false;
    this.password = '';
    this.currentStep = LoginSteps.emailSearch;
  };

  logInViaSAML = (): void => {
    this._redirectToSAML();
  };

  _displayConfigIncompleteError = (): void => {
    // User can't use credentials and the SSO url is not configured. Show error
    this.flashMessages.showFlash('error', 'SAML configuration incomplete. Contact your admin.');
  };

  _displayPasswordForm(): void {
    this.password = '';
    this.emailFound = true;
  }

  #displayMFAChallenge(): void {
    this.currentStep = LoginSteps.mfaChallenge;
  }

  _displayResendConfirmationEmail(): void {
    this.currentStep = LoginSteps.emailFound;
  }

  _redirectToSAML(): void {
    this.localStorage.setItem('default_route', '');
    this.windowLocation.assign(this.samlUrl!);
  }

  <template>
    <div class="flex items-center" style="height:100vh">
      <div class="mx-auto w-full md:w-auto">
        <a href="https://envoy.com">
          <img class="authenticate-logo" src="/assets/images/logo.svg" alt="Envoy" />
        </a>

        <UiCard class="!px-4 md:!px-8 !pt-8 !pb-4 md:min-w-[420px] m-4 md:m-0">
          <LiquidBind @value={{this.currentStep}}>
            {{#if (eq this.currentStep "emailSearch")}}
              <LoginWizardEmailSearch
                @authenticateTask={{this.authenticateTask}}
                @companyId={{@companyId}}
                @email={{this.email}}
                @emailFound={{this.emailFound}}
                @password={{this.password}}
                @prefilledEmail={{@prefilledEmail}}
                @rd={{@rd}}
                @samlUrl={{this.samlUrl}}
                @changeEmail={{this.changeEmail}}
                @handleSearchResult={{this.handleSearchResult}}
                @redirectToEnvoy={{@redirectToEnvoy}}
                @logInViaSAML={{this.logInViaSAML}}
              />
            {{else if (eq this.currentStep "emailFound")}}
              <LoginWizardEmailFound @email={{this.email}} @changeEmail={{this.changeEmail}} />
            {{else if (eq this.currentStep "mfaChallenge")}}
              <LoginWizardMfaChallenge
                @changeEmail={{this.changeEmail}}
                @sendMfaCode={{this.sendMfaCode}}
                @verifyMfaCode={{this.verifyMfaCode}}
              />
            {{/if}}
          </LiquidBind>
        </UiCard>
      </div>
    </div>
  </template>
}
